import { DateTime } from 'luxon';

export const Formatter = {
  // Format timestamp to readable date/time
  Timestamp: (timestamp) => {
    if (!timestamp) return '-';

    try {
      // Try parsing as ISO first
      let dt = DateTime.fromISO(timestamp);

      if (!dt.isValid) {
        // Try parsing as SQL format (YYYY-MM-DD HH:mm:ss)
        dt = DateTime.fromSQL(timestamp);
      }

      if (!dt.isValid) {
        // Try parsing as milliseconds
        const dtFromMs = DateTime.fromMillis(parseInt(timestamp));
        if (dtFromMs.isValid) {
          return dtFromMs.toFormat('dd/MM/yyyy HH:mm:ss');
        }
        return timestamp; // Return original if parsing fails
      }
      return dt.toFormat('dd/MM/yyyy HH:mm:ss');
    } catch (error) {
      console.warn('Error formatting timestamp:', timestamp, error);
      return timestamp;
    }
  },

  // Format duration in seconds to MM:SS or HH:MM:SS
  Time: (seconds) => {
    if (!seconds || seconds < 0) return '00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  },

  // Format date only
  Date: (timestamp) => {
    if (!timestamp) return '-';

    try {
      let dt = DateTime.fromISO(timestamp);
      if (!dt.isValid) {
        dt = DateTime.fromSQL(timestamp);
      }
      if (!dt.isValid) {
        const dtFromMs = DateTime.fromMillis(parseInt(timestamp));
        if (dtFromMs.isValid) {
          return dtFromMs.toFormat('dd/MM/yyyy');
        }
        return timestamp;
      }
      return dt.toFormat('dd/MM/yyyy');
    } catch (error) {
      console.warn('Error formatting date:', timestamp, error);
      return timestamp;
    }
  },

  // Format time only
  TimeOnly: (timestamp) => {
    if (!timestamp) return '-';

    try {
      let dt = DateTime.fromISO(timestamp);
      if (!dt.isValid) {
        dt = DateTime.fromSQL(timestamp);
      }
      if (!dt.isValid) {
        const dtFromMs = DateTime.fromMillis(parseInt(timestamp));
        if (dtFromMs.isValid) {
          return dtFromMs.toFormat('HH:mm:ss');
        }
        return timestamp;
      }
      return dt.toFormat('HH:mm:ss');
    } catch (error) {
      console.warn('Error formatting time:', timestamp, error);
      return timestamp;
    }
  },

  // Format percentage
  Percentage: (value, decimals = 0) => {
    if (value === null || value === undefined || isNaN(value)) return '0%';
    return `${Number(value).toFixed(decimals)}%`;
  },

  // Format score with color coding
  Score: (score) => {
    if (score === null || score === undefined || isNaN(score)) return '-';
    
    const numScore = Number(score);
    let colorClass = '';
    
    if (numScore >= 80) {
      colorClass = 'text-green-600';
    } else if (numScore >= 60) {
      colorClass = 'text-yellow-600';
    } else {
      colorClass = 'text-red-600';
    }
    
    return {
      value: numScore.toFixed(0),
      colorClass
    };
  },

  // Format compliance status
  Compliance: (isCompliant) => {
    if (isCompliant === true || isCompliant === 'true' || isCompliant === 'Yes') {
      return {
        text: 'Pass',
        colorClass: 'text-green-600'
      };
    } else if (isCompliant === false || isCompliant === 'false' || isCompliant === 'No') {
      return {
        text: 'Fail',
        colorClass: 'text-red-600'
      };
    }
    return {
      text: '- Unknown',
      colorClass: 'text-gray-500'
    };
  },

  // Format the Agent Behavior and Conduct verdict.
  // Undefined means the scorecard asked no conduct questions - shown as a dash rather than a pass,
  // since nothing was assessed. Rating.Conduct defaults to true, so callers should pass
  // `Result?.Conduct` directly and let this handle the absent case.
  Conduct: (conductPassed) => {
    if (conductPassed === false || conductPassed === 'false' || conductPassed === 'No') {
      return {
        text: 'Fail',
        colorClass: 'text-red-600'
      };
    } else if (conductPassed === true || conductPassed === 'true' || conductPassed === 'Yes') {
      return {
        text: 'Pass',
        colorClass: 'text-green-600'
      };
    }
    return {
      text: '—',
      colorClass: 'text-gray-400'
    };
  },

  // Format sentiment score
  Sentiment: (score) => {
    if (score === null || score === undefined || isNaN(score)) return { text: '-', colorClass: 'text-gray-500' };
    
    const numScore = Number(score);
    
    if (numScore >= 70) {
      return {
        text: 'Positive',
        colorClass: 'text-green-600',
        score: numScore
      };
    } else if (numScore >= 40) {
      return {
        text: 'Neutral',
        colorClass: 'text-yellow-600',
        score: numScore
      };
    } else {
      return {
        text: 'Negative',
        colorClass: 'text-red-600',
        score: numScore
      };
    }
  },

  // Format file size
  FileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes) return '-';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Format currency
  Currency: (amount, currency = 'USD') => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Format number with thousands separator
  Number: (num, decimals = 0) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  },

  // Format relative time (e.g., "2 hours ago")
  RelativeTime: (timestamp) => {
    if (!timestamp) return '-';

    try {
      let dt = DateTime.fromISO(timestamp);
      if (!dt.isValid) {
        dt = DateTime.fromSQL(timestamp);
      }
      if (!dt.isValid) {
        const dtFromMs = DateTime.fromMillis(parseInt(timestamp));
        if (dtFromMs.isValid) {
          return dtFromMs.toRelative();
        }
        return timestamp;
      }
      return dt.toRelative();
    } catch (error) {
      console.warn('Error formatting relative time:', timestamp, error);
      return timestamp;
    }
  },

  // Truncate text with ellipsis
  Truncate: (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  },

  // Format phone number
  Phone: (phoneNumber) => {
    if (!phoneNumber) return '-';
    
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Format US phone number
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    
    // Format international or other formats
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
    }
    
    return phoneNumber; // Return original if can't format
  },

  // Format status with appropriate styling
  Status: (status) => {
    if (!status) return { text: '-', colorClass: 'text-gray-500' };
    
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
      case 'done':
      case 'completed':
      case 'success':
        return { text: status, colorClass: 'text-green-600' };
      case 'processing':
      case 'in progress':
      case 'pending':
        return { text: status, colorClass: 'text-yellow-600' };
      case 'failed':
      case 'error':
      case 'cancelled':
        return { text: status, colorClass: 'text-red-600' };
      default:
        return { text: status, colorClass: 'text-gray-600' };
    }
  }
};